import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';

const counterVariants = cva(
  cn(['rounded-round', 'inline-flex', 'h-16', 'min-w-16', 'items-center', 'justify-center', 'border', 'px-4']),
  {
    variants: {
      knockout: {
        true: 'border-transparent bg-default text-brand',
        false: 'border-transparent bg-brand text-primary-knockout',
      },
      disabled: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        disabled: true,
        class: 'border-default bg-default text-disabled',
      },
    ],
    defaultVariants: {
      knockout: false,
      disabled: false,
    },
  },
);

export type CounterProps = Omit<ComponentProps<'span'>, 'children'> &
  VariantProps<typeof counterVariants> & {
    children?: never;
    number: string | number;
    knockout?: boolean;
    disabled?: boolean;
  };

/**
 * Counter badge rendered as a circle (single digit) or pill (multi-digit).
 *
 * Uses two nested spans because `text-box-trim` only works on block containers —
 * it has no effect on flex containers directly. The outer span handles layout
 * (fixed height, centering), while the inner span handles typography with `text-trim`
 * so flexbox can vertically center the trimmed text box.
 */
export function Counter({ className, knockout = false, disabled = false, number, ...props }: CounterProps) {
  return (
    <span
      data-slot="counter"
      aria-disabled={disabled || undefined}
      className={cn(counterVariants({ knockout, disabled, className }))}
      {...props}
    >
      <span className="label-sm text-trim">{number}</span>
    </span>
  );
}
